import React, { FC, useEffect, useRef, useState } from 'react';

import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Launch from '@mui/icons-material/Launch';
import PollIcon from '@mui/icons-material/Poll';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { CheckIcon, ClockIcon } from 'assets/icons';
import useCurrentUser from 'hooks/useCurrentUser';
import { useDeleteScheduledMessageMutation } from 'services/api/scheduledMessages.api';
import { Message } from 'services/types/message';
import type { ScheduledMessage } from 'services/types/scheduledMessage';
import { extractFilenameFromUrl, getFileExtensionFromUrl } from 'utils/fileUtils';
import textToReactNodes from 'utils/textToReactNodes';

import EvalModal from '../../../pages/Evals/EvalModal';

const filterOutStatuses = ['neverSent', 'deleted', 'discarded', 'failed'];

const getExtension = (fileUrl: string): string => {
  // Use the utility function for getting file extension from URLs, including signed URLs
  return getFileExtensionFromUrl(fileUrl);
};

interface Props {
  conversation: Message[];
  scheduledMessages?: ScheduledMessage[];
  isFilteredConversation: boolean;
  fullHeight?: boolean;
  isClientView?: boolean;
  isAssistantWriting?: boolean;
}

const Conversation: FC<Props> = ({
  conversation,
  scheduledMessages,
  isFilteredConversation,
  fullHeight,
  isClientView,
  isAssistantWriting,
}) => {
  const isWhatsAppError = (error: string): boolean => {
    return error.includes('131049') || error.includes('130472') || error.includes('131026');
  };

  const [deleteScheduledMessage] = useDeleteScheduledMessageMutation();
  const filteredConversation = conversation
    .filter(
      (message) =>
        !isFilteredConversation ||
        !filterOutStatuses.includes(message.status) ||
        (message.status === 'failed' && message.error && isWhatsAppError(message.error))
    )
    .map((message) => ({
      ...message,
      referenceMessage: message.referenceMessageId
        ? conversation.find((m) => m.id === message.referenceMessageId)
        : null,
    }));
  const [openEvalModal, setOpenEvalModal] = useState(false);
  const [evalMessage, setEvalMessage] = useState<null | Message>(null);
  const currentUser = useCurrentUser();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView();
  }, [conversation]);

  const getMessageError = (error: string): React.ReactNode | null => {
    // 131049 - This message was not delivered to maintain healthy ecosystem engagement. - In order to maintain a healthy ecosystem engagement, the message failed to be delivered.
    // 130472 - User's number is part of an experiment - Failed to send message because this user's phone number is part of an experiment
    if (error.includes('131049') || error.includes('130472')) {
      const errorCode = error.includes('131049') ? '131049' : '130472';
      return (
        <>
          El mensaje no pudo ser enviado porque no se pudo entregar a la red de WhatsApp.{' '}
          <a
            href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes?locale=es_ES#otros-errores"
            target="_blank"
            rel="noreferrer"
          >
            Busca el código de error ({errorCode}) aquí para más información.
          </a>
        </>
      );
    }
    // 131026 - Message undeliverable - Message Undeliverable.
    if (error.includes('131026')) {
      return 'El mensaje no pudo ser enviado porque el número de teléfono no tiene WhatsApp.';
    }
    return null;
  };

  const getMessageButtons = (buttons: Message['buttons']) =>
    buttons && (
      <Stack pb={1} spacing={1}>
        {buttons.map((button) => (
          <div key={`${button.id} - ${button.title}`}>
            <Divider />
            <Box textAlign="center" alignSelf="center">
              {button.url ? (
                <Button
                  variant="text"
                  startIcon={<Launch fontSize="small" />}
                  href={button.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {button.title}
                </Button>
              ) : (
                button.title
              )}
            </Box>
          </div>
        ))}
      </Stack>
    );

  const getMessageHTML = (message: Message) => {
    let text = '';
    if (message.role === 'assistant' && message.functionName && message.functionArguments) {
      text += `Asistente: ${message.functionName}(${message.functionArguments?.replaceAll('\n', '')})`;
    } else if (message.role === 'function') {
      text += `Función: ${message.text?.replaceAll('\\', '').replace(/(^")|("$)/g, '')}`;
    } else {
      text += message.text ?? '';
    }
    text += '\n';

    const isSpecialStatus = [
      'failed',
      'deleted',
      'discarded',
      'warning',
      'functionSuggestion',
      'functionResponse',
      'suggestion',
      'neverSent',
    ].includes(message.status);

    return (
      <>
        {message.referenceMessage && (
          <Box
            p={1}
            mb={1}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderLeft: '4px solid #25D366',
              borderRadius: '4px',
              color: '#667781',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {message.referenceMessage.fileUrl &&
              (['png', 'jpeg', 'jpg', 'webp'].includes(getExtension(message.referenceMessage.fileUrl)) ? (
                <img
                  src={message.referenceMessage.fileUrl}
                  alt="chat-file"
                  style={{
                    maxHeight: '70px',
                    objectFit: 'contain',
                    marginRight: '10px',
                  }}
                />
              ) : (
                <InsertDriveFileIcon
                  sx={{
                    height: '1.8em',
                    width: '1.8em',
                  }}
                />
              ))}
            {message.referenceMessage.text && message.referenceMessage.text.length > 100
              ? `${message.referenceMessage.text?.substring(0, 100)}...`
              : message.referenceMessage.text}
            {!message.referenceMessage.text && message.referenceMessage.fileName && (
              <Typography>{message.referenceMessage.fileName.split('-').at(-1)}</Typography>
            )}
          </Box>
        )}
        {['system', 'none'].includes(message.sender as string) &&
          currentUser?.isAdmin &&
          message.status !== 'event' && (
            <div
              style={{
                position: 'relative',
                float: 'right',
                top: '0px',
              }}
            >
              <IconButton
                color="inherit"
                size="small"
                title="Evaluar este mensaje"
                onClick={() => {
                  setOpenEvalModal(true);
                  setEvalMessage(message);
                }}
              >
                <PollIcon fontSize="inherit" />
              </IconButton>
              <a
                href={`/playground/${message.id}`}
                style={{ textDecoration: 'none', margin: 0, padding: 0 }}
                target="_blank"
                rel="noreferrer"
                title="Replicar este mensaje en el playground"
              >
                &gt;
              </a>
            </div>
          )}

        {message.fileUrl && ['ogg'].includes(getExtension(message.fileUrl)) && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <audio controls src={message.fileUrl} />
        )}
        {text && (!message.fileUrl || getExtension(message.fileUrl) === 'ogg') && (
          <Typography
            variant="body1"
            px={1}
            sx={{
              whiteSpace: 'pre-line',
              textAlign: 'left',
              wordBreak: 'break-word',
              color: isSpecialStatus ? 'grey' : undefined,
            }}
          >
            {['discarded', 'neverSent'].includes(message.status) ? text : textToReactNodes(text)}
          </Typography>
        )}
        {message.url && (
          <a href={message.url} target="_blank" rel="noreferrer">
            <Typography variant="body2" px={1}>
              {message.fileUrl}
            </Typography>
          </a>
        )}
        {message.fileUrl && ['png', 'jpeg', 'jpg', 'webp', 'mp4'].includes(getExtension(message.fileUrl)) && (
          <>
            {getExtension(message.fileUrl) === 'mp4' && (
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <video width="100%" autoPlay muted controls>
                <source src={message.fileUrl} type="video/mp4" />
              </video>
            )}
            {getExtension(message.fileUrl) !== 'mp4' && (
              <img
                src={message.fileUrl}
                alt="chat-file"
                style={{ maxWidth: '100%', maxHeight: message.fileUrl.includes('webp') ? '120px' : '250px' }}
              />
            )}
            <br />
            {message.fileUrl.includes('referral') && <CampaignIcon color="primary" />}
            {text && (
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: 'pre-line',
                  textAlign: 'left',
                  wordBreak: 'break-word',
                  color: isSpecialStatus ? 'grey' : undefined,
                }}
              >
                {textToReactNodes(
                  text
                    .replace(/Imagen enviada: \S+\.(png|jpeg|jpg|webp)/, '')
                    .replace('Imagen enviada', '')
                    .trim()
                )}
              </Typography>
            )}
          </>
        )}
        {message.fileUrl && !['png', 'jpeg', 'jpg', 'webp', 'mp4', 'ogg'].includes(getExtension(message.fileUrl)) && (
          <Box
            p={1}
            sx={{
              backgroundColor: message.role === 'user' ? '#f3f5f5' : '#d8f3ce',
              borderRadius: '10px',
            }}
          >
            <Button
              href={message.fileUrl}
              target="_blank"
              rel="noreferrer"
              variant="text"
              color="inherit"
              sx={{ textTransform: 'none', width: '100%', display: 'block' }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                  <InsertDriveFileIcon
                    sx={{
                      height: '1.8em',
                      width: '1.8em',
                    }}
                  />
                  <Stack pr={10} pl={1} textAlign="left">
                    <Typography variant="body1" sx={{ color: '#111b21' }}>
                      {message.text
                        ? message.text
                            .split(':')
                            .pop()
                            ?.replace(/[a-z0-9]*-/g, '')
                        : extractFilenameFromUrl(message.fileUrl)}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#111b21' }} fontSize={15}>
                      {getExtension(message.fileUrl).toLocaleUpperCase()}
                    </Typography>
                  </Stack>
                </Box>
                <DownloadIcon
                  sx={{
                    border: 'solid',
                    borderRadius: '100%',
                    borderWidth: 'thin',
                    opacity: '80%',
                    padding: '2px',
                    height: '1.6em',
                    width: '1.6em',
                  }}
                />
              </Box>
            </Button>
          </Box>
        )}
        <Box display="inline-flex" alignItems="center">
          <Typography variant="body2" p={1} sx={{ color: 'grey' }}>
            {message.user?.name && `Enviado por ${message.user?.name} - `}
            {new Date(message.createdAt).toLocaleTimeString('es-CL', {
              day: 'numeric',
              month: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}
          </Typography>
          {message.status === 'sent' && <CheckIcon />}
          {(message.status === 'delivered' || message.status === 'received') && (
            <>
              <CheckIcon sx={{ marginRight: '-16px' }} />
              <CheckIcon />
            </>
          )}
          {message.status === 'read' && (
            <>
              <CheckIcon color="info" sx={{ marginRight: '-16px' }} />
              <CheckIcon color="info" />
            </>
          )}
          {message.status === 'sent to api' && currentUser && <ClockIcon />}
          {isSpecialStatus && <Typography variant="body2">{message.status}</Typography>}
          {currentUser?.isAdmin && !isFilteredConversation && message.cost > 0 && (
            <Typography variant="body2" p={1} sx={{ color: 'grey', fontSize: '0.7em' }}>
              ${message.cost.toFixed(2)} {message.AIModel}
            </Typography>
          )}
          {message.emoji && (
            <div
              style={{
                height: '26px',
                width: '30px',
                position: 'relative',
                float: 'right',
                bottom: '-20px',
                backgroundColor: 'white',
                borderRadius: '50px',
                textAlign: 'center',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              {message.emoji}
            </div>
          )}
        </Box>
        {getMessageButtons(message.buttons)}
        {message.error && (currentUser?.isAdmin || isWhatsAppError(message.error)) && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              textAlign: 'center',
              padding: '4px',
            }}
          >
            {currentUser?.isAdmin ? message.error : getMessageError(message.error)}
          </div>
        )}
      </>
    );
  };

  const getMessageStyle = (message: Message) => {
    if (message.status === 'event') {
      return {
        backgroundColor: 'secondary.light',
        textAlign: 'center',
        alignSelf: 'center',
      };
    }
    if ((!isClientView && message.role !== 'user') || (message.role === 'user' && isClientView)) {
      return {
        backgroundColor: '#d9fdd2',
        textAlign: 'end',
        alignSelf: 'flex-end',
      };
    }
    return {
      backgroundColor: '#fff',
      textAlign: 'start',
      alignSelf: 'flex-start',
    };
  };

  return (
    <Box height={fullHeight ? '100%' : undefined} display="flex" flexDirection="column" m={1}>
      {filteredConversation?.map((message) => (
        <Box
          key={message.id}
          mt={1}
          p={1}
          pb={0}
          sx={{
            maxWidth: '70%',
            borderRadius: '12px',
            boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
            ...getMessageStyle(message),
          }}
        >
          {getMessageHTML(message)}
        </Box>
      ))}
      {scheduledMessages &&
        scheduledMessages
          .filter((sm) => sm.status !== 'sent')
          .sort((a, b) => a.postAt.localeCompare(b.postAt))
          .map((scheduledMessage) => (
            <Box
              key={scheduledMessage.id}
              mt={1}
              pb={0}
              sx={{
                maxWidth: '70%',
                textAlign: 'end',
                alignSelf: 'flex-end',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <IconButton
                size="small"
                onClick={() => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm('¿Estás seguro de que quieres eliminar este mensaje programado?')) {
                    deleteScheduledMessage({
                      clientId: scheduledMessage.clientId,
                      scheduledMessageId: scheduledMessage.id,
                    })
                      .then(() => {
                        // eslint-disable-next-line no-alert
                        alert(
                          'Mensaje programado eliminado correctamente. Recargue la conversación para ver el cambio.'
                        );
                      })
                      .catch(console.error); // eslint-disable-line no-console
                  }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: '#e0e0e0',
                  },
                }}
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
              <Box
                sx={{
                  p: 1,
                  borderRadius: '12px',
                  boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
                  backgroundColor: '#d9fdd2',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="body1"
                  px={1}
                  sx={{
                    whiteSpace: 'pre-line',
                    textAlign: 'left',
                    wordBreak: 'break-word',
                  }}
                >
                  {textToReactNodes(scheduledMessage.text)}
                </Typography>
                <Box display="inline-flex" pt={1} justifyContent="flex-end">
                  <Typography variant="body2" sx={{ color: 'grey', mr: 1 }}>
                    {new Date(scheduledMessage.postAt).toLocaleTimeString('es-CL', {
                      day: 'numeric',
                      month: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    })}
                  </Typography>
                  <ClockIcon fontSize="small" />
                  {scheduledMessage.status !== 'pending' && (
                    <Typography variant="body2">{scheduledMessage.status}</Typography>
                  )}
                </Box>
                {scheduledMessage.buttons && getMessageButtons(scheduledMessage.buttons)}
              </Box>
            </Box>
          ))}
      {isAssistantWriting && (
        <Box
          m={1}
          p={1}
          pb={0}
          sx={{ maxWidth: '70%', borderRadius: '12px', backgroundColor: '#f3f5f5', textAlign: 'start' }}
        >
          <Typography variant="body1" px={1}>
            Escribiendo...
          </Typography>
        </Box>
      )}
      {openEvalModal && evalMessage && (
        <EvalModal setOpen={setOpenEvalModal} messageText={evalMessage.text || ''} messageId={evalMessage.id} />
      )}
      <div ref={messagesEndRef} style={{ minHeight: '10px', opacity: 0 }} />
    </Box>
  );
};

Conversation.defaultProps = {
  fullHeight: false,
  isClientView: false,
  isAssistantWriting: false,
  scheduledMessages: [],
};

export default Conversation;
